// Delivery-point picker — "where did this fruit come in?" at intake.
//
// RENDER-ON-DATA-PRESENCE: returns null when the tenant has defined no points.
// That is what keeps this invisible for every existing packhouse (and all
// citrus intake) until an operator sets one up in Config → Commodities →
// Delivery points — no new required field appears on a screen that didn't ask
// for one. The column is NULLable for the same reason.
//
// COMMODITY-AGNOSTIC by design: the list is never filtered by the delivery's
// commodity. One point receives strawberries and blueberries on the same day —
// coupling the picker to commodity would re-introduce exactly the constraint
// docs/specs/dynamic-packhouse-topology.md exists to remove.
//
// Value is the delivery_point_id; the NAME snapshot is stamped server-side
// (mig 20260727090100), so callers only ever send the id.

import { useDeliveryPoints } from '@/lib/usePartyOptions'

export default function DeliveryPointPicker({
  value, onChange, label = 'Delivery point', required = false, disabled = false,
}: {
  value: string | null
  onChange: (deliveryPointId: string | null) => void
  label?: string
  /** Operator-facing nudge only — the column is deliberately never NOT NULL. */
  required?: boolean
  disabled?: boolean
}) {
  const { data: points = [] } = useDeliveryPoints()

  // No points configured → this packhouse doesn't work this way. Render nothing.
  if (points.length === 0) return null

  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-gray-500">
        {label}{required && <span className="text-red-400"> *</span>}
      </span>
      <select
        className="field text-sm mt-0.5"
        value={value ?? ''}
        disabled={disabled}
        onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
      >
        <option value="">— not recorded —</option>
        {points.map(p => (
          <option key={p.delivery_point_id} value={p.delivery_point_id}>
            {p.code ? `${p.code} · ${p.name}` : p.name}
          </option>
        ))}
      </select>
    </label>
  )
}
